import { useState, useEffect } from 'react';
import { useAuth } from '../App';
import './PricingPage.css';

const PricingPage = () => {
    const { token, user } = useAuth();
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentSub, setCurrentSub] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                let plansRes = await fetch('/api/subscriptions/plans', { headers: { 'Authorization': `Bearer ${token}` } });

                // Fallback to billing alias if subscriptions is 404
                if (plansRes.status === 404) {
                    console.log('[Pricing] /api/subscriptions 404, trying /api/billing...');
                    plansRes = await fetch('/api/billing/plans', { headers: { 'Authorization': `Bearer ${token}` } });
                }

                let subRes = await fetch('/api/subscriptions/me', { headers: { 'Authorization': `Bearer ${token}` } });
                if (subRes.status === 404) {
                    subRes = await fetch('/api/billing/me', { headers: { 'Authorization': `Bearer ${token}` } });
                }

                const plansData = await plansRes.json();
                const subData = await subRes.json();

                if (Array.isArray(plansData)) {
                    setPlans(plansData);
                } else if (plansData.error) {
                    setError(plansData.error);
                }

                setCurrentSub(subData);
            } catch (err) {
                console.error('Failed to fetch pricing data:', err);
                setError('Could not load subscription plans. Please try again later.');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [token]);

    const handleUpgrade = async (planId) => {
        try {
            const res = await fetch('/api/subscriptions/upgrade', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ planId })
            });
            if (!res.ok) {
                let errorMsg = 'Payment service unavailable';
                try {
                    const errorData = await res.json();
                    errorMsg = errorData.message || errorData.error || errorMsg;
                } catch (e) {
                    // Ignore JSON parse error on non-ok response
                }
                throw new Error(errorMsg);
            }

            const data = await res.json();

            // Open Paystack
            if (typeof window.PaystackPop === 'undefined') {
                throw new Error('Paystack Payment Gateway failed to load. Please check your internet connection.');
            }

            // Open Paystack using the access_code generated by the server
            // We do NOT need to pass amount/email again because the transaction is already initialized
            const handler = window.PaystackPop.setup({
                key: import.meta.env.VITE_PAYSTACK_PUBLIC_KEY,
                access_code: data.access_code,
                callback: (response) => {
                    window.location.reload(); // Refresh to update plan status
                },
                onClose: () => {
                    console.log('Payment window closed');
                }
            });
            handler.openIframe();
        } catch (err) {
            console.error('Upgrade failed:', err);
            // Wait for user to see the error potentially, or just alert
            alert(`Payment initialization failed: ${err.message || 'Unknown error'}`);
        }
    };

    if (loading) return <div className="pricing-loading">Loading Plans...</div>;
    if (error) return (
        <div className="pricing-page">
            <header className="pricing-header">
                <h1>Service Unavailable</h1>
                <p className="error-message">{error}</p>
                <button className="btn btn-secondary" onClick={() => window.location.reload()}>Retry</button>
            </header>
        </div>
    );

    return (
        <div className="pricing-page">
            <header className="pricing-header">
                <h1>Choose Your Plan</h1>
                <p>Industrial-strength automation for Finance, Property, and Education.</p>
            </header>

            <div className="pricing-grid">
                {plans.map(plan => (
                    <div key={plan.id} className={`pricing-card ${plan.id} ${currentSub?.plan_id === plan.id ? 'current' : ''}`}>
                        {currentSub?.plan_id === plan.id && <div className="current-badge">Active Plan</div>}
                        <div className="card-header">
                            <h2 className="plan-name">{plan.name}</h2>
                            <div className="plan-price">
                                <span className="currency">GH₵</span>
                                <span className="amount">{(plan.price_monthly_cents / 100).toFixed(0)}</span>
                                <span className="period">/mo</span>
                            </div>
                        </div>

                        <div className="industry-badge">
                            {plan.id === 'starter' && 'Solopreneurs'}
                            {plan.id === 'professional' && 'Boutique Firms'}
                            {plan.id === 'enterprise' && 'Large Organizations'}
                            {plan.id === 'institutional' && 'Institutional'}
                        </div>

                        <ul className="feature-list">
                            <li>
                                <strong>{plan.features.max_templates === 999 ? 'Unlimited' : plan.features.max_templates}</strong> Templates
                            </li>
                            <li>{plan.features.branding ? '✅ Custom Branding' : '❌ Standard Branding'}</li>
                            <li>{plan.features.audit_logs ? '✅ Full Audit History' : '❌ 3-Day History'}</li>
                            {plan.features.api_access && <li>✅ Developer API Access</li>}
                        </ul>

                        <button
                            className="btn-plan-action"
                            onClick={() => handleUpgrade(plan.id)}
                            disabled={currentSub?.plan_id === plan.id || plan.id === 'starter'}
                        >
                            {currentSub?.plan_id === plan.id ? 'Current Plan' : 'Select Plan'}
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default PricingPage;
